import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, unref as _unref, createBlock as _createBlock, createStaticVNode as _createStaticVNode } from "vue"
import _imports_0 from '@/assets/udi-logo.png'


const _hoisted_1 = { class: "contenedor" }
const _hoisted_2 = {
  class: "documento",
  id: "documento-pdf"
}
const _hoisted_3 = { class: "seccion-titulo" }
const _hoisted_4 = { class: "doc-titulo" }
const _hoisted_5 = {
  key: 0,
  class: "tabla-toolbar"
}
const _hoisted_6 = {
  key: 1,
  class: "doc-table"
}
const _hoisted_7 = { key: 2 }
const _hoisted_8 = { class: "doc-error" }
const _hoisted_9 = { key: 3 }
const _hoisted_10 = { class: "botones" }
const _hoisted_11 = ["disabled"]

import { ref, reactive, onMounted, defineComponent, h, PropType, VNode } from 'vue'
import { useRoute } from 'vue-router'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import type Componente from '@/interfaces/Componente'
import type EstructuraModelo from '@/interfaces/EstructuraModelo'
import { useComponentes } from '@/store/GestionComponentes'
import { useModelo } from '@/store/GestionModelo'
import { useGestionVersiones } from '@/store/GestionVersiones'
import { limpiarNiveles } from '@/utils/nivelesHelper'
import { podarArbolInactivo } from '@/utils/estructuraActiva'

const AZUL = '#0e69a0'
const AMARILLO = '#eea222'  

export default /*@__PURE__*/_defineComponent({
  __name: 'ModeloDocumento',
  setup(__props) {

/**
 * Presentación documental del modelo, lista para imprimir.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `Documento/:id_modelo/:id_version` (PresentarDocumento) — permiso `modelos.ver`
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

const route = useRoute()
const idModelo = Number(route.params.id_modelo ?? NaN)
const idVersion = Number(route.params.id_version ?? NaN)
const storeComponentes = useComponentes()
const storeModelo = useModelo()
const storeVersion = useGestionVersiones()
const componentes = ref<Componente[]>([])
const nombreModelo = ref('')
const nombresNiveles = ref<string[]>([])
const descargando = ref(false)
const error = ref<string | null>(null)
const colapsados = reactive<Record<number, boolean>>({})


function colorNivel(nivel: number): string {
  // nivel 1 = azul fuerte; niveles siguientes cada vez más claros
  const tints: Record<number, string> = {
    1: AZUL,
    2: '#dceaf4',
    3: '#eef5fb',
    4: '#f6f9fc'
  }
  return tints[nivel] || '#ffffff'
}
function textoNivel(nivel: number): string {
  return nivel === 1 ? '#ffffff' : (nivel <= 3 ? '#0c447c' : '#185fa5')
}
// Versiones RGB para jsPDF
function rgbNivel(nivel: number): [number, number, number] {
  const map: Record<number, [number, number, number]> = {
    1: [14, 105, 160],
    2: [220, 234, 244],
    3: [238, 245, 251],
    4: [246, 249, 252]
  }
  return map[nivel] || [255, 255, 255]
}
function rgbTextoNivel(nivel: number): [number, number, number] {
  return nivel === 1 ? [255, 255, 255] : (nivel <= 3 ? [12, 68, 124] : [24, 95, 165])
}


/// para expandir
function toggleNodo(id: number) {
  colapsados[id] = !colapsados[id]
}
function expandirTodo() {
  Object.keys(colapsados).forEach(k => { colapsados[Number(k)] = false })
}
function contraerTodo() {
  const marcar = (arr: Componente[]) => {
    arr.forEach(c => {
      if (c.children && c.children.length) {
        if (c.id_componente != null) colapsados[c.id_componente] = true
        marcar(c.children)
      }
    })
  }
  marcar(componentes.value)
}

function ordenarPorOrdinal(lista: Componente[]): Componente[] {
  return [...lista].sort((a, b) => {
    const pa = (a.ordinal ?? '').split('.').map(s => parseInt(s) || 0)
    const pb = (b.ordinal ?? '').split('.').map(s => parseInt(s) || 0)
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      const d = (pa[i] ?? 0) - (pb[i] ?? 0)
      if (d !== 0) return d
    }
    return 0
  })
}

function ordenarRecursivo(nodos: Componente[]): Componente[] {
  return ordenarPorOrdinal(nodos).map(n => ({
    ...n,
    children: n.children ? ordenarRecursivo(n.children) : []
  }))
}

/// PDF
async function descargar() {
  if (descargando.value) return
  descargando.value = true

  try {
    const doc = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait' })
    const marginLeft = 60
    const marginTop = 140
    const pageWidth = doc.internal.pageSize.getWidth()

    const img = new Image()
    img.src = '/udi-logo.png'
    await new Promise((resolve, reject) => {
      img.onload = () => resolve(true)
      img.onerror = () => reject(new Error('Error cargando imagen'))
    })

    const head = [['Nivel', 'Componente', 'Peso']]
    const body: { row: (string | number)[], nivel: number }[] = []

    const recorrer = (nodo: Componente, nivel = 1) => {
      const nombreNivel = nombresNiveles.value[nivel - 1] ?? `Nivel ${nivel}`
      const nivelTexto = `${nombreNivel} ${nodo.ordinal ?? ''}`.trim()
      const sangria = '    '.repeat(Math.max(0, nivel - 1))
      body.push({
        row: [
          nivelTexto,
          `${sangria}${nodo.nombre}`,
          nodo.peso != null && !isNaN(Number(nodo.peso)) ? Number(nodo.peso).toFixed(2) + '%' : ''
        ],
        nivel
      })
      if (nodo.children) nodo.children.forEach(hijo => recorrer(hijo, nivel + 1))
    }
    componentes.value.forEach(c => recorrer(c))

    autoTable(doc, {
      head,
      body: body.map(b => b.row),
      startY: marginTop + 20,
      margin: { top: marginTop, bottom: 60, left: marginLeft, right: marginLeft },
      styles: {
        fontSize: 10,
        cellPadding: { left: 10, top: 6, bottom: 6 },
        overflow: 'linebreak',
        lineColor: [255, 255, 255],
        lineWidth: 1
      },
      headStyles: {
        fillColor: [14, 105, 160],
        textColor: [255, 255, 255],
        halign: 'left',
        fontStyle: 'bold',
        cellPadding: { left: 10, top: 7, bottom: 7 }
      },
      columnStyles: {
        0: { cellWidth: 110, halign: 'left' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 70, halign: 'right', cellPadding: { right: 14, top: 6, bottom: 6, left: 6 } }
      },
      didParseCell: (data) => {
        if (data.section === 'body') {
          const item = body[data.row.index]
          const nivel = item.nivel
          data.cell.styles.fillColor = rgbNivel(nivel)
          data.cell.styles.textColor = rgbTextoNivel(nivel)
          if (nivel === 1) data.cell.styles.fontStyle = 'bold'
        }
      },
      didDrawCell: (data) => {
        // Barra de acento amarilla a la izquierda de la columna Nivel (col 0)
        if (data.section === 'body' && data.column.index === 0) {
          const nivel = body[data.row.index].nivel
          if (nivel <= 3) {
            const alpha = nivel === 1 ? 1 : 0.45
            const bg = rgbNivel(nivel)
            doc.setFillColor(
              Math.round(238 * alpha + bg[0] * (1 - alpha)),
              Math.round(162 * alpha + bg[1] * (1 - alpha)),
              Math.round(34  * alpha + bg[2] * (1 - alpha))
            )
            doc.rect(data.cell.x, data.cell.y, 3, data.cell.height, 'F')
          }
        }
      },
      didDrawPage: (data) => {
        if (data.pageNumber === 1) {
          doc.addImage(img, 'PNG', marginLeft, 40, 50, 30)
          doc.setFont('helvetica', 'bold')
          doc.setFontSize(13)
          doc.setTextColor(0, 0, 0)
          const textX = marginLeft + 60
          doc.text('UNIVERSIDAD', textX, 50)
          doc.text('DE INVESTIGACIÓN', textX, 65)
          doc.text('Y DESARROLLO', textX, 80)

          doc.setFontSize(14)
          doc.text(nombreModelo.value, pageWidth / 2, 115, { align: 'center' })
        }

        const pageCount = doc.getNumberOfPages()
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.setTextColor(90, 90, 90)
        doc.text(
          `Página ${data.pageNumber} de ${pageCount}`,
          pageWidth - marginLeft,
          doc.internal.pageSize.getHeight() - 20,
          { align: 'right' }
        )
      }
    })

    doc.save('modelo-autoevaluacion.pdf')
  } catch (err) {
    console.error('Error generando PDF:', err)
  } finally {
    descargando.value = false
  }
}

onMounted(async () => {
  window.scrollTo({ top: 0, behavior: 'auto' })

  if (!Number.isFinite(idModelo) || idModelo <= 0) {
    error.value = `Parámetro id_modelo inválido: ${idModelo}`
    return
  }

  // Peticiones independientes en paralelo: datos del modelo y raíz del árbol
  const [modelo, raiz] = await Promise.all([
    storeModelo.getModeloById(idModelo) as Promise<EstructuraModelo | null>,
    storeComponentes.cargarRaizPorModelo(idModelo)
  ])

  if (modelo) {
    nombreModelo.value = modelo.nombre
    if (modelo.niveles) nombresNiveles.value = limpiarNiveles(modelo.niveles)
  }

  if (!raiz?.id_componente) {
    error.value = 'No se encontró la raíz del modelo.'
    return
  }

  // Peticiones independientes en paralelo: árbol completo e IDs activos de versión
  // getIdsComponentesActivos solo se llama cuando hay una versión válida para evitar 404
  const [, activos] = await Promise.all([
    storeComponentes.cargarArbolDesdeRaiz(raiz.id_componente),
    idVersion > 0
      ? storeVersion.getIdsComponentesActivos(idVersion)
      : Promise.resolve(new Set<number>())
  ])

  const raizCompleta = storeComponentes.componentes.find(c => c.id_componente === raiz.id_componente)
  if (!raizCompleta?.children?.length) {
    error.value = 'El modelo no tiene componentes.'
    return
  }

  componentes.value = podarArbolInactivo(ordenarRecursivo(raizCompleta.children), activos)
})

/* Componente recursivo de fila — con caret colapsable, sangría, color y */
const ArbolDocumento = defineComponent({
  name: 'ArbolDocumento',
  props: {
    nodo: { type: Object as PropType<Componente>, required: true },
    nivel: { type: Number, required: true },
    nombresNiveles: { type: Array as PropType<string[]>, required: true },
    colapsados: { type: Object as PropType<Record<number, boolean>>, required: true }
  },
  emits: ['toggle'],
  setup(props, { emit }): () => VNode[] {
    return () => {
      const filas: VNode[] = []
      const nivel = props.nivel
      const bg = colorNivel(nivel)
      const color = textoNivel(nivel)
      const tieneHijos = !!(props.nodo.children && props.nodo.children.length)
      const estaColapsado = props.nodo.id_componente != null ? !!props.colapsados[props.nodo.id_componente] : false
      const padLeft = 16 + (nivel - 1) * 22
      const esFactor = nivel === 1
      const accent =
        esFactor ? `4px solid ${AMARILLO}`
        : (nivel <= 3 ? '4px solid rgba(238,162,34,.45)' : 'none')

      const caret = tieneHijos
        ? h('i', {
            class: 'ti ti-chevron-down',
            style: {
              cursor: 'pointer',
              fontSize: '15px',
              marginRight: '6px',
              display: 'inline-block',
              transition: 'transform .15s',
              transform: estaColapsado ? 'rotate(-90deg)' : 'rotate(0deg)',
              verticalAlign: '-2px'
            },
            onClick: () => emit('toggle', props.nodo.id_componente)
          })
        : h('span', { style: { display: 'inline-block', width: '21px' } })

      const pesoTexto =
        props.nodo.peso != null && !isNaN(Number(props.nodo.peso))
          ? Number(props.nodo.peso).toFixed(2) + '%'
          : ''

      const nombreNivel = props.nombresNiveles[nivel - 1] ?? `Nivel ${nivel}`
      const nivelTexto = `${nombreNivel} ${props.nodo.ordinal ?? ''}`.trim()

      filas.push(
        h('tr', { style: { backgroundColor: bg, borderBottom: esFactor ? '2px solid #fff' : '1px solid #fff' } }, [
          h('td', {
            style: {
              color,
              textAlign: 'left',
              fontVariantNumeric: 'tabular-nums',
              fontWeight: esFactor ? '600' : '400',
              whiteSpace: 'nowrap',
              padding: '10px 16px',
              borderLeft: accent
            }
          }, nivelTexto),
          h('td', {
            style: {
              paddingLeft: `${padLeft}px`,
              paddingTop: '10px',
              paddingBottom: '10px',
              paddingRight: '12px',
              color,
              fontWeight: esFactor ? '600' : '400',
            }
          }, [caret, h('span', props.nodo.nombre)]),
          h('td', {
            style: {
              color,
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
              padding: '10px 24px 10px 12px'
            }
          }, pesoTexto)
        ])
      )

      if (tieneHijos && !estaColapsado) {
        const hijosOrdenados = [...props.nodo.children!].sort((a, b) => {
          const pa = (a.ordinal ?? '').split('.').map(s => parseInt(s) || 0)
          const pb = (b.ordinal ?? '').split('.').map(s => parseInt(s) || 0)
          for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
            const d = (pa[i] ?? 0) - (pb[i] ?? 0)
            if (d !== 0) return d
          }
          return 0
        })
        hijosOrdenados.forEach(hijo => {
          filas.push(
            h(ArbolDocumento, {
              nodo: hijo,
              nivel: nivel + 1,
              nombresNiveles: props.nombresNiveles,
              colapsados: props.colapsados,
              onToggle: (id: number) => emit('toggle', id)
            })
          )
        })
      }
      return filas
    }
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[2] || (_cache[2] = _createStaticVNode("<header class=\"doc-header\" data-v-47ec0504><img src=\"" + _imports_0 + "\" alt=\"Logo UDI\" class=\"logo\" data-v-47ec0504><div class=\"header-texto\" data-v-47ec0504><h1 class=\"nombre-univeridad\" data-v-47ec0504><span data-v-47ec0504>UNIVERSIDAD</span><span class=\"line-indent\" data-v-47ec0504>DE INVESTIGACIÓN</span><span class=\"line-indent\" data-v-47ec0504>Y DESARROLLO</span></h1></div></header>", 1)),
      _createElementVNode("section", _hoisted_3, [
        _createElementVNode("h1", _hoisted_4, _toDisplayString(nombreModelo.value), 1)
      ]),
      (componentes.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createElementVNode("button", {
              class: "btn salir",
              onClick: expandirTodo
            }, "Expandir todo"),
            _createElementVNode("button", {
              class: "btn salir",
              onClick: contraerTodo
            }, "Contraer todo")
          ]))
        : _createCommentVNode("", true),
      (componentes.value.length)
        ? (_openBlock(), _createElementBlock("section", _hoisted_6, [
            _createElementVNode("table", null, [
              _cache[0] || (_cache[0] = _createElementVNode("thead", null, [
                _createElementVNode("tr", null, [
                  _createElementVNode("th", { class: "col-nivel" }, "Nivel"),
                  _createElementVNode("th", { class: "col-nombre" }, "Componente"),
                  _createElementVNode("th", { class: "col-peso" }, "Peso")
                ])
              ], -1)),
              _createElementVNode("tbody", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(componentes.value, (comp) => {
                  return (_openBlock(), _createBlock(_unref(ArbolDocumento), {
                    key: comp.id_componente,
                    nodo: comp,
                    nivel: 1,
                    "nombres-niveles": nombresNiveles.value,
                    colapsados: colapsados,
                    onToggle: toggleNodo
                  }, null, 8, ["nodo", "nombres-niveles", "colapsados"]))
                }), 128))
              ])
            ])
          ]))
        : (error.value)
          ? (_openBlock(), _createElementBlock("section", _hoisted_7, [
              _createElementVNode("p", _hoisted_8, _toDisplayString(error.value), 1)
            ]))
          : (_openBlock(), _createElementBlock("section", _hoisted_9, _cache[1] || (_cache[1] = [
              _createElementVNode("p", null, "Cargando componentes...", -1)
            ])))
    ]),
    _createElementVNode("div", _hoisted_10, [
      _createElementVNode("button", {
        class: "btn guardar",
        onClick: descargar,
        disabled: descargando.value
      }, _toDisplayString(descargando.value ? 'Generando...' : 'Descargar'), 9, _hoisted_11)
    ])
  ]))
}
}

})